import React, { useContext } from 'react';
import {
    IPjmRegistrationsSidebarFiltersContext,
    IPjmRegistrationsSidebarFilters,
    PjmRegistrationSidebarFiltersContext,
} from './PjmRegistrationSidebarFiltersContext';
import { CollapseFilterWrapper } from '../../../../../components/sidebar-filters/CollapseFilterWrapper';
import { useSidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { PjmRegistrationResourceTypeFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmRegistrationResourceTypeFilterSelect';
import { PjmRegistrationProductFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmRegistrationProductFilterSelect';
import { PjmRegistrationLeadTimeFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmRegistrationLeadTimeFilterSelect';
import { PjmRegistrationCompletedFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmRegistrationCompletedFilterSelect';
import { PjmRegistrationUnderDuplicateReviewFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmRegistrationUnderDuplicateReviewFilterSelect';
import { PjmSyncDrhubStatusFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmSyncDrhubStatusFilterSelect';
import { PjmZoneFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmZoneFilterSelect';
import { PjmEdcFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmEdcFilterSelect';
import { PjmStatusFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmStatusFilterSelect';
import { PjmRegistrationSeasonFilterSelect } from '../../../../../components/sidebar-filters/filter-select/PjmRegistrationSeasonFilterSelect';

export const PjmRegistrationFilterKeys = {
    // Filters
    season: 'season',
    status: 'summaryInfo.status',
    zone: 'summaryInfo.zone',
    edc: 'summaryInfo.EDC.name',
    resourceType: 'capacityInfo.resourceType',
    product: 'capacityInfo.product',
    leadTime: 'capacityInfo.leadTime',
    syncDrhubStatus: 'syncDrhubStatus',

    // options
    completed: 'summaryInfo.completed',
    underDuplicateReview: 'summaryInfo.underDuplicateReview',
} as const;

export type TPjmRegistrationFilterKey = (typeof PjmRegistrationFilterKeys)[keyof typeof PjmRegistrationFilterKeys];

export const PjmRegistrationFilterLabels = {
    [PjmRegistrationFilterKeys.season]: 'Season',
    [PjmRegistrationFilterKeys.status]: 'Status',
    [PjmRegistrationFilterKeys.zone]: 'Zone',
    [PjmRegistrationFilterKeys.edc]: 'EDC',
    [PjmRegistrationFilterKeys.resourceType]: 'Resource Type',
    [PjmRegistrationFilterKeys.product]: 'Product',
    [PjmRegistrationFilterKeys.leadTime]: 'Lead Time',
    [PjmRegistrationFilterKeys.completed]: 'Completed',
    [PjmRegistrationFilterKeys.underDuplicateReview]: 'Under Duplicate Review',
    [PjmRegistrationFilterKeys.syncDrhubStatus]: 'DRHub Sync',
} as const;

interface IPjmRegistrationsSidebarFiltersProps {
    onFilterChange: (filters: IPjmRegistrationsSidebarFilters) => void;
}

export const PjmRegistrationsSidebarFilters: React.FC<IPjmRegistrationsSidebarFiltersProps> = ({ onFilterChange }) => {
    const { filters, handleFilterChange } = useContext<IPjmRegistrationsSidebarFiltersContext>(PjmRegistrationSidebarFiltersContext);
    const {
        isLoading: isFiltersLoading,
        pjmRegistrationSeasonOptions,
        pjmRegistrationStatusOptions,
        pjmRegistrationZoneOptions,
        pjmRegistrationEdcOptions,
        pjmRegistrationResourceTypeOptions,
        pjmRegistrationProductOptions,
        pjmRegistrationLeadTimeOptions,
        pjmRegistrationCompletedOptions,
        pjmRegistrationUnderDuplicateReviewOptions,
        pjmRegistrationSyncDrhubStatusOptions,
    } = useSidebarFilterOptionsContext();

    const handlePjmRegistrationsFilterChange = (key: string, value: unknown) => {
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">
                <PjmRegistrationSeasonFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.season]}
                    value={filters[PjmRegistrationFilterKeys.season] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.season, value)}
                    options={pjmRegistrationSeasonOptions}
                    isLoading={isFiltersLoading}
                />
                <PjmStatusFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.status]}
                    value={filters[PjmRegistrationFilterKeys.status] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.status, value)}
                    options={pjmRegistrationStatusOptions}
                    isLoading={false}
                />
                <PjmZoneFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.zone]}
                    value={filters[PjmRegistrationFilterKeys.zone] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.zone, value)}
                    options={pjmRegistrationZoneOptions}
                    isLoading={false}
                />
                <PjmEdcFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.edc]}
                    value={filters[PjmRegistrationFilterKeys.edc] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.edc, value)}
                    options={pjmRegistrationEdcOptions}
                    isLoading={false}
                />
                <PjmRegistrationResourceTypeFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.resourceType]}
                    value={filters[PjmRegistrationFilterKeys.resourceType] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.resourceType, value)}
                    options={pjmRegistrationResourceTypeOptions}
                    isLoading={false}
                />
                <PjmRegistrationProductFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.product]}
                    value={filters[PjmRegistrationFilterKeys.product] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.product, value)}
                    options={pjmRegistrationProductOptions}
                    isLoading={false}
                />
                <PjmRegistrationLeadTimeFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.leadTime]}
                    value={filters[PjmRegistrationFilterKeys.leadTime] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.leadTime, value)}
                    options={pjmRegistrationLeadTimeOptions}
                    isLoading={false}
                />
                <PjmSyncDrhubStatusFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.syncDrhubStatus]}
                    value={filters[PjmRegistrationFilterKeys.syncDrhubStatus] || []}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.syncDrhubStatus, value)}
                    options={pjmRegistrationSyncDrhubStatusOptions}
                    isLoading={false}
                />
            </CollapseFilterWrapper>
            <CollapseFilterWrapper label="Options">
                <PjmRegistrationCompletedFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.completed]}
                    value={filters[PjmRegistrationFilterKeys.completed] || null}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.completed, value)}
                    options={pjmRegistrationCompletedOptions}
                    isLoading={false}
                />
                <PjmRegistrationUnderDuplicateReviewFilterSelect
                    label={PjmRegistrationFilterLabels[PjmRegistrationFilterKeys.underDuplicateReview]}
                    value={filters[PjmRegistrationFilterKeys.underDuplicateReview] || null}
                    onChange={value => handlePjmRegistrationsFilterChange(PjmRegistrationFilterKeys.underDuplicateReview, value)}
                    options={pjmRegistrationUnderDuplicateReviewOptions}
                    isLoading={false}
                />
            </CollapseFilterWrapper>
        </div>
    );
};
